import { create } from 'zustand';
import type { Item } from './canvasStore';

// History action types
export type HistoryActionType = 
  | 'CREATE_ITEM'
  | 'DELETE_ITEM'
  | 'DELETE_ITEMS'
  | 'MOVE_ITEM'
  | 'MOVE_ITEMS'
  | 'UPDATE_CONTENT'
  | 'MOVE_TO_BOARD';

export interface HistoryAction {
  type: HistoryActionType;
  timestamp: number;
  // For create/delete
  items?: Item[];
  // For move (position changes)
  itemId?: string;
  previousPositions?: Map<string, { x: number; y: number }>;
  newPositions?: Map<string, { x: number; y: number }>;
  // For content updates
  previousContent?: Record<string, unknown>;
  newContent?: Record<string, unknown>;
  // For board moves
  itemIds?: string[];
  previousBoardId?: string;
  newBoardId?: string;
}

interface HistoryState {
  undoStack: HistoryAction[];
  redoStack: HistoryAction[];
  maxHistorySize: number;
  
  // Actions
  pushAction: (action: HistoryAction) => void;
  undo: () => HistoryAction | null;
  redo: () => HistoryAction | null;
  canUndo: () => boolean;
  canRedo: () => boolean;
  clear: () => void;
}

export const useHistoryStore = create<HistoryState>((set, get) => ({
  undoStack: [],
  redoStack: [],
  maxHistorySize: 5, // Strict limit of 5 steps
  
  pushAction: (action) => {
    set((state) => {
      const newStack = [...state.undoStack, action];
      // Keep only the last 5 actions
      while (newStack.length > state.maxHistorySize) {
        newStack.shift();
      }
      return {
        undoStack: newStack,
        redoStack: [], // Clear redo stack on new action
      };
    });
  },
  
  undo: () => {
    const { undoStack } = get();
    if (undoStack.length === 0) return null;
    
    const action = undoStack[undoStack.length - 1];
    
    set((state) => ({
      undoStack: state.undoStack.slice(0, -1),
      redoStack: [...state.redoStack, action],
    }));
    
    return action;
  },
  
  redo: () => {
    const { redoStack } = get();
    if (redoStack.length === 0) return null;
    
    const action = redoStack[redoStack.length - 1];
    
    set((state) => ({
      redoStack: state.redoStack.slice(0, -1),
      undoStack: [...state.undoStack, action],
    }));
    
    return action;
  },
  
  canUndo: () => get().undoStack.length > 0,
  
  canRedo: () => get().redoStack.length > 0,
  
  clear: () => set({ undoStack: [], redoStack: [] }),
}));
